/** A tiled dot-grid paper, built once and reused as a canvas pattern. */

import { token } from '../engine/palette.ts';
import type { Camera } from '../lib/scene.ts';
import { toScreen } from '../lib/scene.ts';

const TILE = 32;
let tile: HTMLCanvasElement | null = null;

export function paperPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
  if (typeof document === 'undefined') return null;
  if (!tile) {
    tile = document.createElement('canvas');
    tile.width = TILE;
    tile.height = TILE;
    const tctx = tile.getContext('2d');
    if (!tctx) return null;
    tctx.fillStyle = token('--paper');
    tctx.fillRect(0, 0, TILE, TILE);
    tctx.fillStyle = token('--paper-line');
    tctx.beginPath();
    tctx.arc(TILE / 2, TILE / 2, 1.4, 0, Math.PI * 2);
    tctx.fill();
  }
  return ctx.createPattern(tile, 'repeat');
}

/**
 * Paper that goes on forever: the dots ride with the camera so panning feels
 * like sliding a big sheet rather than moving marks across a fixed background.
 *
 * The grid steps by powers of two as you zoom, holding the dot spacing in a
 * comfortable band — otherwise zooming out turns the paper into a grey moiré
 * and zooming in leaves one lonely dot on screen.
 */
export function fillPaper(ctx: CanvasRenderingContext2D, cam: Camera, width: number, height: number): void {
  const pattern = paperPattern(ctx);
  if (!pattern || typeof DOMMatrix === 'undefined' || !pattern.setTransform) {
    ctx.fillStyle = pattern ?? token('--paper');
    ctx.fillRect(0, 0, width, height);
    return;
  }

  let step = 1;
  while (TILE * step * cam.scale < 22) step *= 2;
  while (TILE * step * cam.scale > 56) step /= 2;

  const origin = toScreen(cam, 0, 0, width, height);
  const k = step * cam.scale;
  pattern.setTransform(new DOMMatrix([k, 0, 0, k, origin.x, origin.y]));
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, width, height);
}
